import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import {
  chartLayoutColours,
  getColorStyles,
} from '@risksmart-app/components/src/utils/colours';
import { Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type Highcharts from 'highcharts';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { HighchartsWidget } from '../../HighchartsWidget';
import { NoWidgetData } from '../NoWidgetData';
import { WidgetLoading } from '../widget-loading';
import type { CellClickData, HeatmapCellData } from './types';

type Props = {
  loading?: boolean;
  data: HeatmapCellData[][];
  onCellClick?: (data: CellClickData) => void;
  controlType: Risk_Assessment_Result_Control_Type_Enum;
};

export const HeatmapWidgetHighcharts: FC<Props> = ({
  data,
  loading,
  onCellClick,
  controlType,
}) => {
  const { t } = useTranslation(['common'], {
    keyPrefix:
      controlType === Risk_Assessment_Result_Control_Type_Enum.Controlled
        ? 'dashboard.widgets.controlledRiskHeatMap'
        : 'dashboard.widgets.uncontrolledRiskHeatMap',
  });
  const { getLabelByIndex: getImpactLabelByIndex } = useRating('impact');
  const { getLabelByIndex: getLikelihoodLabelByIndex } =
    useRating('likelihood');

  const seriesData = useMemo(
    () =>
      data.flatMap((row, y) =>
        row.map((cell, x) => ({
          x,
          y,
          value: 1, // Static value since we're using direct colors and not automatic interpolation with colorAxis
          color: getColorStyles(cell.background).backgroundColor,
          cellData: cell,
        }))
      ),
    [data]
  );

  if (loading) {
    return <WidgetLoading />;
  }

  if (!data || data.length === 0) {
    return <NoWidgetData />;
  }

  const options: Highcharts.Options = {
    chart: {
      type: 'heatmap',
      plotBorderWidth: 0,
    },
    subtitle: {
      text: '',
    },
    legend: {
      enabled: false,
    },
    xAxis: {
      lineWidth: 0,
      labels: {
        enabled: false,
      },
      minorTickLength: 0,
      tickLength: 0,
      title: {
        text: t('xAxisTitle'),
      },
    },
    yAxis: {
      labels: {
        enabled: false,
      },
      title: {
        text: t('yAxisTitle'),
      },
    },
    tooltip: {
      formatter: function () {
        if (this.x === undefined || this.y === undefined) {
          return;
        }
        const cellData = data[this.y][this.x];

        return (
          `<b>${t('popover.likelihood')}:</b><br />${getLikelihoodLabelByIndex(this.y)}<br /><br />` +
          `<b>${t('popover.impact')}:</b><br />${getImpactLabelByIndex(this.x)}<br /><br />` +
          `<b>${t('popover.recordCount')}:</b><br />${cellData.value}<br /><br />` +
          (cellData.label
            ? `<b>${t('popover.label')}:</b><br />${cellData.label}`
            : '')
        );
      },
    },
    series: [
      {
        type: 'heatmap',
        borderWidth: 5,
        borderRadius: 5,
        borderColor: chartLayoutColours.seriesBorders,
        data: seriesData,
        dataLabels: {
          enabled: true,
          formatter: function () {
            if (this.x === undefined || this.y === undefined) {
              return;
            }
            const cellData = data[this.y][this.x];

            return cellData.value;
          },
        },
        point: {
          events: {
            click: function () {
              if (this.x === undefined || this.y === undefined) {
                return;
              }
              const cellData = data[this.y][this.x];
              onCellClick?.({
                data: cellData,
                x: this.x,
                y: this.y,
              });
            },
          },
        },
      },
    ],
  };

  return <HighchartsWidget options={options} />;
};
